import { useQuery } from "@apollo/client";
import { Alert, Box, Divider, Link, Skeleton, Stack, TextField, Typography } from "@mui/material";
import { NotificationMarkdown } from "components/Notifications/NotificationMarkdown";
import { RightDrawerShell } from "components/RightDrawerShell/RightDrawerShell";
import { gql } from "generated-graphql";
import { StaffNotificationDetailQuery } from "generated-graphql/graphql";
import { ReactNode, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { prettyPrintDateTimeMed } from "util/dates";

import { NotSentTable, Recipient, SentToTable } from "./NotificationRecipientTables";

const STAFF_NOTIFICATION_DETAIL = gql(`
  query StaffNotificationDetail($id: ID!) {
    staffNotificationDetail(id: $id) {
      notification {
        id
        title
        bodyMarkdown
        createdAt
        resolvedAt
        tenant {
          id
          name
        }
        facility {
          id
          name
        }
        subject {
          type
          id
          route
          label
          liveStatus {
            status
          }
        }
      }
      kindTitle
      createdBy
      resolvedBy
      recipientsRecorded
      emailByLegacyDigest
      recipients {
        userId
        name
        email
        isStaff
        inclusionReasons
        exclusionReason
        inApp
        inAppSuppressedBy
        readAt
        emailMode
        emailSuppressedBy
        emailOutcome
        emailReason
        emailHeldReason
        emailAttemptedAt
        emailAttempts
        messageId
        earlierEmail {
          messageId
          sentAt
        }
      }
    }
  }
`);

const EMPTY = "—";

/**
 * Who set it off. A person with no alert kind behind it queued it by hand; with a kind,
 * it was their action that the alert fired on. Otherwise it's the emitting service.
 */
function createdByText(detail: Pick<Detail, "createdBy" | "kindTitle">): string {
  if (!detail.createdBy.includes("@")) {
    return detail.createdBy;
  }
  return detail.kindTitle ? `Action by ${detail.createdBy}` : `Queued by ${detail.createdBy}`;
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Stack spacing={0.5}>
      <Typography variant="overline" color="text.secondary">
        {title}
      </Typography>
      <Box>{children}</Box>
    </Stack>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Stack direction="row" spacing={2} sx={{ py: 0.5 }}>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ minWidth: 130, flexShrink: 0, pt: 0.25 }}
      >
        {label}
      </Typography>
      <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
    </Stack>
  );
}

function TextFact({ label, value }: { label: string; value?: string | null }) {
  return (
    <Fact label={label}>
      <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
        {value || EMPTY}
      </Typography>
    </Fact>
  );
}

/** Above this many people, the drawer offers a box to find one of them. */
const FIND_PERSON_THRESHOLD = 10;

function personName(recipient: Recipient): string {
  return recipient.name ?? recipient.email ?? "";
}

function matchesPerson(recipient: Recipient, query: string): boolean {
  const needle = query.trim().toLowerCase();
  return (
    !needle ||
    personName(recipient).toLowerCase().includes(needle) ||
    (recipient.email ?? "").toLowerCase().includes(needle)
  );
}

function FactSkeletons({ count }: { count: number }) {
  return (
    <>
      {Array.from({ length: count }, (_, index) => (
        <Stack key={index} direction="row" spacing={2} sx={{ py: 0.5 }}>
          <Skeleton width={110} />
          <Skeleton width="45%" />
        </Stack>
      ))}
    </>
  );
}

/** The drawer's shape while it loads: the facts, then a couple of recipients. */
function DetailSkeleton() {
  return (
    <Stack spacing={2} divider={<Divider flexItem />}>
      <Stack spacing={0.5}>
        <Skeleton width={90} />
        <FactSkeletons count={6} />
      </Stack>
      <Stack spacing={0.5}>
        <Skeleton width={80} />
        {[0, 1].map((index) => (
          <Stack key={index} spacing={0.25} sx={{ py: 1.25 }}>
            <Skeleton width={160} />
            <Skeleton width={220} />
            <Skeleton width={180} />
            <Skeleton width={260} />
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
}

type Detail = NonNullable<StaffNotificationDetailQuery["staffNotificationDetail"]>;

function resolutionText(detail: Detail): string {
  const { resolvedAt } = detail.notification;
  if (!resolvedAt) {
    return "Open";
  }
  const when = `Resolved ${prettyPrintDateTimeMed(resolvedAt)}`;
  return detail.resolvedBy ? `${when} by ${detail.resolvedBy}` : when;
}

type Subject = NonNullable<Detail["notification"]["subject"]>;

/** Jobs have no customer page, but staff can open one on the Jobs screen. */
function subjectHref(subject: Subject): string | null {
  if (subject.route) {
    return subject.route;
  }
  return subject.type === "JOB" ? `/staff/jobs/${encodeURIComponent(subject.id)}` : null;
}

function LinkedRecord({ subject }: { subject: Subject | null | undefined }) {
  if (!subject) {
    return <Typography variant="body2">{EMPTY}</Typography>;
  }
  const href = subjectHref(subject);
  const liveStatus = subject.liveStatus?.status;
  return (
    <Typography variant="body2">
      {href ? (
        <Link component={RouterLink} to={href}>
          {subject.label}
        </Link>
      ) : (
        subject.label || EMPTY
      )}
      {liveStatus ? ` · ${liveStatus}` : ""}
    </Typography>
  );
}

function NotificationFacts({ detail }: { detail: Detail }) {
  const { notification } = detail;
  return (
    <Section title="Notification">
      <TextFact label="Organization" value={notification.tenant?.name} />
      <TextFact label="Facility" value={notification.facility?.name} />
      <Fact label="Linked record">
        <LinkedRecord subject={notification.subject} />
      </Fact>
      <TextFact label="Sent" value={prettyPrintDateTimeMed(notification.createdAt)} />
      <TextFact label="Status" value={resolutionText(detail)} />
      <Fact label="Triggered by">
        <Typography variant="body2">{detail.kindTitle || EMPTY}</Typography>
        <Typography variant="caption" color="text.secondary">
          {createdByText(detail)}
        </Typography>
      </Fact>
    </Section>
  );
}

/** Keyed by notification, so the email being viewed resets when the drawer retargets. */
/** "Sent to (6) · 2 read · 4 emailed", counted over everyone, whatever the search. */
function sentToTitle(sent: Recipient[]): string {
  const read = sent.filter((recipient) => recipient.readAt).length;
  // An email sent with a copy this one replaced still reached them about this alert.
  const emailed = sent.filter(
    (recipient) => recipient.emailOutcome === "SENT" || recipient.earlierEmail
  ).length;
  return `Sent to (${sent.length}) · ${read} read · ${emailed} emailed`;
}

function NotificationDetailBody({ detail }: { detail: Detail }) {
  const [personQuery, setPersonQuery] = useState("");
  const shown = detail.recipients.filter((recipient) => matchesPerson(recipient, personQuery));
  const sent = shown.filter((recipient) => !recipient.exclusionReason);
  const notSent = shown.filter((recipient) => recipient.exclusionReason);
  const allSent = detail.recipients.filter((recipient) => !recipient.exclusionReason);
  const sentTotal = allSent.length;
  const notSentTotal = detail.recipients.length - sentTotal;

  return (
    <Stack spacing={2} divider={<Divider flexItem />}>
      <NotificationFacts detail={detail} />

      <Section title="What it said">
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
          {detail.notification.title}
        </Typography>
        <NotificationMarkdown bodyMarkdown={detail.notification.bodyMarkdown} />
      </Section>

      {detail.recipients.length > FIND_PERSON_THRESHOLD && (
        <TextField
          size="small"
          label="Find a person"
          placeholder="Name or email"
          value={personQuery}
          onChange={(event) => setPersonQuery(event.target.value)}
        />
      )}

      <Section title={sentToTitle(allSent)}>
        {!detail.recipientsRecorded && (
          <Alert severity="info" sx={{ mb: 1 }}>
            This was sent before email and exclusion details were recorded, so only in-app receipts
            are listed.
          </Alert>
        )}
        {sent.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {sentTotal === 0 ? "Nobody received it." : "Nobody here matches."}
          </Typography>
        ) : (
          <SentToTable recipients={sent} byLegacyDigest={detail.emailByLegacyDigest} />
        )}
      </Section>

      {notSent.length > 0 && (
        <Section title={`Not sent (${notSentTotal})`}>
          <NotSentTable recipients={notSent} />
        </Section>
      )}

      {detail.recipientsRecorded && (
        <Typography variant="caption" color="text.secondary">
          Anyone not listed wasn't in this notification's audience, for example customers without
          access to the facility, deactivated accounts, or staff who don't follow the organization.
        </Typography>
      )}
    </Stack>
  );
}

export function NotificationDetailDrawer({
  notificationId,
  onClose,
}: {
  notificationId: string | null;
  onClose: () => void;
}) {
  const { data, loading, error } = useQuery(STAFF_NOTIFICATION_DETAIL, {
    variables: { id: notificationId ?? "" },
    skip: !notificationId,
    fetchPolicy: "cache-and-network",
  });
  const detail = data?.staffNotificationDetail ?? null;
  const notFound = Boolean(notificationId && data && !detail && !error);
  const loadingDetail = loading && !detail && !error;
  let title: ReactNode = "Notification";
  if (detail) {
    title = detail.notification.title;
  } else if (loadingDetail) {
    title = <Skeleton width={220} />;
  }

  return (
    <RightDrawerShell open={notificationId !== null} onClose={onClose} title={title}>
      {error && (
        <Alert severity="error">
          Failed to load this notification. Close the drawer and try again.
        </Alert>
      )}
      {notFound && !loading && (
        <Alert severity="error">This notification could not be found. The link may be stale.</Alert>
      )}
      {loadingDetail && <DetailSkeleton />}
      {detail && <NotificationDetailBody key={detail.notification.id} detail={detail} />}
    </RightDrawerShell>
  );
}
